'use client';

import { BadgeCheck, Calculator, CheckCircle2, Home, PiggyBank, RotateCcw, ShieldCheck, WalletCards } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'students-housing-budget-planner-v1';

const defaultValues = {
  rent: '25000',
  deposit: '25000',
  commission: '0',
  utilities: '4000',
  internet: '700',
  transport: '2500',
  household: '8000',
  monthlyBudget: '55000',
};

const moneyFields = [
  { key: 'rent', label: 'Аренда', hint: 'Сколько платишь за жильё каждый месяц' },
  { key: 'deposit', label: 'Залог', hint: 'Обычно возвращается, но нужен при въезде' },
  { key: 'commission', label: 'Комиссия', hint: 'Если есть агент или сервис подбора' },
  { key: 'utilities', label: 'Коммунальные', hint: 'Свет, вода, отопление и другие платежи' },
  { key: 'internet', label: 'Интернет', hint: 'Домашний интернет или связь для учёбы' },
  { key: 'transport', label: 'Транспорт', hint: 'Дорога до корпуса, работы и обратно' },
  { key: 'household', label: 'Бытовые покупки', hint: 'Посуда, текстиль, бытовая химия, мелкий ремонт' },
  { key: 'monthlyBudget', label: 'Месячный бюджет студента', hint: 'Все деньги на месяц: стипендия, помощь, работа' },
] as const;

const checklistTasks = [
  'Проверил документы собственника или условия общежития.',
  'Не перевожу залог до просмотра и понятных документов.',
  'Зафиксировал сумму аренды, коммунальные, сроки и возврат залога.',
  'Сделал фото состояния жилья, техники, сантехники и счётчиков.',
  'Проверил дорогу до корпуса утром и вечером.',
  'Сохранил контакты хозяина, УК/ТСЖ, коменданта или аварийной службы.',
] as const;

type FieldKey = keyof typeof defaultValues;
type FormState = Record<FieldKey, string>;
type ChecklistState = Record<string, boolean>;

type StoredState = {
  values?: Partial<FormState>;
  checklist?: ChecklistState;
};

function parseMoney(value: string) {
  const normalized = value.replace(/[^0-9]/g, '');
  return Number(normalized || 0);
}

function formatMoney(value: number) {
  return new Intl.NumberFormat('ru-RU').format(Math.max(0, Math.round(value)));
}

function readStoredState(): StoredState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function getSafetyLabel(monthlyTotal: number, monthlyBudget: number) {
  if (monthlyBudget <= 0) {
    return {
      title: 'Укажи месячный бюджет',
      text: 'Так калькулятор сможет показать, насколько аренда безопасна для студенческого бюджета.',
      tone: 'border-white/10 bg-white/5 text-slate-100',
    };
  }

  const ratio = monthlyTotal / monthlyBudget;

  if (ratio <= 0.45) {
    return {
      title: 'Бюджет выглядит спокойным',
      text: 'Ежемесячные расходы на жильё и дорогу не съедают большую часть бюджета. Всё равно оставь резерв на еду, учёбу и непредвиденные траты.',
      tone: 'border-emerald-300/30 bg-emerald-300/10 text-emerald-100',
    };
  }

  if (ratio <= 0.65) {
    return {
      title: 'Можно жить, но нужен контроль',
      text: 'Расходы уже заметные. Лучше заранее разделить деньги по неделям и оставить запас хотя бы на коммунальные и продукты.',
      tone: 'border-main/35 bg-main/10 text-main',
    };
  }

  return {
    title: 'Рискованный вариант',
    text: 'Жильё забирает слишком большую часть бюджета. Проверь комнату, общежитие, другой район или вариант с соседями.',
    tone: 'border-rose-300/35 bg-rose-300/10 text-rose-100',
  };
}

export default function HousingBudgetPlanner() {
  const [values, setValues] = useState<FormState>(defaultValues);
  const [checklist, setChecklist] = useState<ChecklistState>({});
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const stored = readStoredState();
    setValues({ ...defaultValues, ...stored.values });
    setChecklist(stored.checklist ?? {});
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (!isReady) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ values, checklist }));
  }, [values, checklist, isReady]);

  const numbers = useMemo(
    () => Object.fromEntries(Object.entries(values).map(([key, value]) => [key, parseMoney(value)])) as Record<FieldKey, number>,
    [values],
  );

  const firstMonthTotal =
    numbers.rent +
    numbers.deposit +
    numbers.commission +
    numbers.utilities +
    numbers.internet +
    numbers.transport +
    numbers.household;

  const monthlyTotal = numbers.rent + numbers.utilities + numbers.internet + numbers.transport;
  const remainingAfterMonthly = numbers.monthlyBudget - monthlyTotal;
  const safety = getSafetyLabel(monthlyTotal, numbers.monthlyBudget);
  const checkedCount = checklistTasks.filter((task) => checklist[task]).length;
  const checklistPercent = Math.round((checkedCount / checklistTasks.length) * 100);

  function updateField(key: FieldKey, value: string) {
    setValues((current) => ({ ...current, [key]: value.replace(/[^0-9]/g, '') }));
  }

  function toggleTask(task: string) {
    setChecklist((current) => ({ ...current, [task]: !current[task] }));
  }

  function resetPlanner() {
    setValues(defaultValues);
    setChecklist({});
  }

  return (
    <section id="housing-budget-planner" className="mt-8 rounded-[32px] border border-main/25 bg-gradient-to-br from-slate-900/55 via-slate-900/40 to-sky-950/55 p-5 shadow-[0_24px_70px_-36px_rgba(250,204,21,0.28)] backdrop-blur-xl sm:p-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-main/35 bg-main/10 px-3 py-1 text-xs font-black text-main">
            <Calculator className="h-4 w-4" />
            Калькулятор аренды
          </div>
          <h2 className="mt-4 text-2xl font-black text-main sm:text-3xl">
            Посчитай первый месяц, ежемесячные расходы и риск для бюджета
          </h2>
          <p className="mt-2 text-sm leading-7 text-slate-100/90 sm:text-base dark:text-slate-200/90">
            Заполни примерные суммы перед просмотром квартиры или комнаты. Данные сохраняются только в браузере,
            без регистрации и без отправки на сервер.
          </p>
        </div>

        <button
          type="button"
          onClick={resetPlanner}
          className="inline-flex w-fit items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-slate-100 transition hover:border-main/50 hover:text-main"
        >
          <RotateCcw className="h-4 w-4" />
          Сбросить расчёт
        </button>
      </div>

      <div className="mt-6 grid gap-5 xl:grid-cols-[minmax(0,1.05fr)_0.95fr]">
        <div className="grid gap-3 sm:grid-cols-2">
          {moneyFields.map((field) => (
            <label
              key={field.key}
              className="rounded-3xl border border-white/10 bg-white/5 p-4 transition hover:border-main/40 hover:bg-white/10"
            >
              <span className="text-sm font-black text-main">{field.label}</span>
              <span className="mt-1 block min-h-[40px] text-xs leading-5 text-slate-100/70">{field.hint}</span>
              <span className="mt-3 flex items-center gap-2 rounded-2xl border border-white/10 bg-slate-950/35 px-3 py-2 text-slate-100">
                <input
                  inputMode="numeric"
                  value={values[field.key]}
                  onChange={(event) => updateField(field.key, event.target.value)}
                  className="min-w-0 flex-1 bg-transparent text-base font-black outline-none placeholder:text-slate-400"
                  placeholder="0"
                />
                <span className="text-sm font-semibold text-slate-100/70">₽</span>
              </span>
            </label>
          ))}
        </div>

        <div className="grid content-start gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <article className="rounded-3xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-100/80">
                <Home className="h-4 w-4 text-main" />
                Нужно на первый месяц
              </div>
              <div className="mt-3 text-3xl font-black text-main">{formatMoney(firstMonthTotal)} ₽</div>
              <p className="mt-2 text-sm leading-6 text-slate-100/80">
                Аренда + залог + комиссия + коммунальные + интернет + транспорт + бытовые покупки.
              </p>
            </article>

            <article className="rounded-3xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-100/80">
                <WalletCards className="h-4 w-4 text-main" />
                Каждый месяц
              </div>
              <div className="mt-3 text-3xl font-black text-main">{formatMoney(monthlyTotal)} ₽</div>
              <p className="mt-2 text-sm leading-6 text-slate-100/80">
                Аренда + коммунальные + интернет + транспорт. Бытовые покупки считаем отдельно для въезда.
              </p>
            </article>
          </div>

          <article className={`rounded-3xl border p-4 ${safety.tone}`}>
            <div className="flex items-center gap-2 text-sm font-black">
              <ShieldCheck className="h-4 w-4" />
              {safety.title}
            </div>
            <p className="mt-2 text-sm leading-6">{safety.text}</p>
            <div className="mt-3 rounded-2xl border border-white/10 bg-slate-950/20 px-4 py-3 text-sm leading-6 text-slate-100/90">
              После жилья и дороги остаётся:{' '}
              <span className="font-black text-main">{formatMoney(Math.max(remainingAfterMonthly, 0))} ₽</span>
              {remainingAfterMonthly < 0 ? ' — бюджет уходит в минус.' : ' на еду, учёбу и личные расходы.'}
            </div>
          </article>

          <article className="rounded-3xl border border-white/10 bg-white/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-black text-main">Чек-лист просмотра квартиры</div>
                <div className="mt-1 text-sm text-slate-100/75">
                  {checkedCount} из {checklistTasks.length} пунктов выполнено
                </div>
              </div>
              <div className="text-2xl font-black text-main">{checklistPercent}%</div>
            </div>
            <div className="mt-4 h-3 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-main transition-all duration-300" style={{ width: `${checklistPercent}%` }} />
            </div>
            <div className="mt-4 grid gap-2">
              {checklistTasks.map((task) => {
                const checkedTask = Boolean(checklist[task]);
                return (
                  <button
                    key={task}
                    type="button"
                    onClick={() => toggleTask(task)}
                    className={`group flex gap-3 rounded-2xl border px-3 py-2 text-left text-sm leading-6 transition hover:border-main/40 hover:bg-white/10 ${
                      checkedTask ? 'border-main/45 bg-main/10 text-slate-100' : 'border-white/10 bg-white/5 text-slate-100/90'
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                        checkedTask ? 'border-main bg-main text-sky-950' : 'border-white/20 text-slate-100 group-hover:text-main'
                      }`}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                    </span>
                    <span>{task}</span>
                  </button>
                );
              })}
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
